import React, { useState, useCallback, useEffect } from "react";
import { useGetAddressCoordinates } from "../../lib/util";
import useDebounce from "../common/useDebounce";

const internationalCodes = [
  { code: "+234", countryCode: "NG" },
  { code: "+1", countryCode: "USA" },
  // Add more codes as needed
];

const haversineDistance = ([lat1, lon1], [lat2, lon2]) => {
  const toRad = (x) => (x * Math.PI) / 180;

  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // in kilometers
};

const DeliveryMethod = ({
  onBack,
  accessPoint,
  onSuccess,
  deliveryInfo,
  deliveryRegion,
  activateBackButton,
  selectedMethod,
  setSelectedMethod,
}) => {
  useEffect(() => {
    activateBackButton(true, () => {
      onBack();
    });
  }, []);

  const { inStorePurchase, pickUpPurchase, deliveryPurchase } =
    accessPoint?.settings || {};

  const defaultRegion =
    deliveryInfo?.deliveryRegion ||
    (deliveryRegion && deliveryRegion.length > 0
      ? `${deliveryRegion[0]["address"]}-${deliveryRegion[0]["amount"]}`
      : "");
  const [formData, setFormData] = useState({
    deliveryAddress: deliveryInfo?.deliveryAddress || "",
    deliveryRegion: defaultRegion,
    note: deliveryInfo?.note || "",
    intCode: deliveryInfo?.intCode || internationalCodes[0]?.code,
    phone: deliveryInfo?.phone || "",
  });

  const [coordinates, setCoordinates] = useState([]);
  const [isCalculatingClosestRegion, setisCalculatingClosestRegion] =
    useState(false);

  const { mutate: fetchCoordinates, isLoading: isFetching } =
    useGetAddressCoordinates({
      onSuccess: (data) => {
        const [lng, lat] = data.data.geoCode.location.coordinates;
        setCoordinates([lat, lng]);
      },
      onError: (error) => {
        console.log("Failed to get coordinates", error.message);
      },
    });

  const [error, setError] = useState("");
  useEffect(() => {
    if (!formData.phone && deliveryInfo?.phone) {
      setFormData((prev) => ({
        ...prev,
        phone: deliveryInfo.phone,
      }));
    }

    if (!formData.phone && deliveryInfo?.intCode) {
      setFormData((prev) => ({
        ...prev,
        intCode: deliveryInfo.intCode,
      }));
    }
  }, [deliveryInfo?.phone, formData.phone, deliveryInfo?.intCode]);

  const isPhoneValid = (phone) => /^\d+$/.test(phone);

  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  }, []);

  const handleChange = useCallback((e) => {
    setSelectedMethod(e.target.value);
  }, []);

  const handleDeliverySubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!selectedMethod) {
        setError("Please select a delivery method.");
        return;
      }
      if (selectedMethod === "LOCATION") {
        if (!isPhoneValid(formData.phone)) {
          setError("Please enter a valid phone number.");
          return;
        }
        const processedFormData = {
          ...formData,
          phone:
            formData.phone.length >= 11
              ? formData.phone.slice(1)
              : formData.phone,
        };
        onSuccess(processedFormData, selectedMethod);
      } else {
        const processedFormData = {};
        onSuccess(processedFormData, selectedMethod);
      }
      setError("");
    },
    [formData, onSuccess, selectedMethod]
  );
  const debouncedAddress = useDebounce(formData.deliveryAddress, 1000);

  useEffect(() => {
    if (debouncedAddress && deliveryRegion.length > 0) {
      fetchCoordinates({ address: debouncedAddress });
      deliveryRegion.map((region) => {
        const [lng, lat] = region.location.coordinates;
      });
    }
  }, [debouncedAddress]);

  useEffect(() => {
    if (coordinates.length > 0 && deliveryRegion.length > 0) {
      setisCalculatingClosestRegion(true);
      const closestRegion = deliveryRegion.reduce((prev, curr) => {
        const currCoords = curr.location.coordinates;
        const prevCoords = prev.location.coordinates;

        const currDist = haversineDistance(coordinates, [
          currCoords[1],
          currCoords[0],
        ]);
        const prevDist = haversineDistance(coordinates, [
          prevCoords[1],
          prevCoords[0],
        ]);

        return currDist < prevDist ? curr : prev;
      });

      // Update the form region value with the closest match
      setFormData((prev) => ({
        ...prev,
        deliveryRegion: `${closestRegion.address}-${closestRegion.amount}`,
      }));

      setisCalculatingClosestRegion(false);
    }
  }, [coordinates, deliveryRegion]);

  return (
    <>
      <div className="auth-form-container">
        <h2>Delivery Method</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleDeliverySubmit}>
          <div className="radio-box">
            {inStorePurchase === true && (
              <div className="radio-input">
                <label htmlFor="in-store">
                  <input
                    type="radio"
                    id="in-store"
                    value="INSTORE"
                    name="deliveryMethod"
                    checked={selectedMethod === "INSTORE"}
                    onChange={handleChange}
                  />
                  In-store
                </label>
              </div>
            )}
            {pickUpPurchase === true && (
              <div className="radio-input">
                <label htmlFor="pick-up">
                  <input
                    type="radio"
                    id="pick-up"
                    value="PICKUP"
                    name="deliveryMethod"
                    checked={selectedMethod === "PICKUP"}
                    onChange={handleChange}
                  />
                  Pick-up
                </label>
                <p>{accessPoint.address}</p>
              </div>
            )}
            {deliveryPurchase === true && (
              <div className="radio-input">
                <label htmlFor="delivery">
                  <input
                    type="radio"
                    id="delivery"
                    value="LOCATION"
                    name="deliveryMethod"
                    checked={selectedMethod === "LOCATION"}
                    onChange={handleChange}
                  />
                  Delivery
                </label>
              </div>
            )}
          </div>

          {selectedMethod === "LOCATION" && (
            <>
              <div className="input-box">
                <label htmlFor="address">Delivery Address</label>
                <div className="input-text">
                  <input
                    type="text"
                    id="address"
                    name="deliveryAddress"
                    aria-label="Enter your Delivery Address"
                    required
                    placeholder="Delivery Address"
                    value={formData.deliveryAddress}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              {deliveryRegion.length > 0 && (
                <div className="input-box">
                  <label htmlFor="region">Region:</label>
                  <div className="input-text select-div">
                    <select
                      id="region"
                      name="deliveryRegion"
                      value={formData.deliveryRegion}
                      onChange={handleFormChange}
                      aria-label="Select Region"
                      disabled={isFetching || isCalculatingClosestRegion}
                      required
                    >
                      {deliveryRegion.map((region) => (
                        <option
                          key={region.destinationId}
                          value={`${region.address}-${region.amount}`}
                          disabled={isFetching || isCalculatingClosestRegion}
                        >
                          {region.address} ({region.amount})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="input-box">
                <label htmlFor="note">Delivery Note:</label>
                <div className="input-text">
                  <input
                    type="text"
                    id="note"
                    name="note"
                    aria-label="Note for the vendor"
                    placeholder="Note for the vendor..."
                    value={formData.note}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className="input-box">
                <label htmlFor="phone">Phone Number:</label>
                <div className="input-text">
                  <select
                    id="intCode"
                    name="intCode"
                    value={formData.intCode}
                    onChange={handleFormChange}
                    aria-label="Select international code"
                    required
                  >
                    {internationalCodes.map((code) => (
                      <option key={code.code} value={code.code}>
                        {code.code} ({code.countryCode})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    aria-label="Enter your phone number"
                    value={formData.phone}
                    onChange={handleFormChange}
                    required
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>
            </>
          )}

          <div className="login-container">
            <button
              type="submit"
              className="auth-submit-button"
              aria-label="Submit delivery method"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default DeliveryMethod;
